import { useState, useRef, useEffect } from "react";
import { Pencil, UserCheck, QrCode, Trash2, StarOff, MoreHorizontal, Star } from "lucide-react";

/**
 * OwnerToolbar — compact admin/owner controls.
 * DESKTOP/TABLET: small icon buttons in a horizontal row.
 * MOBILE: primary Edit button + "…" menu with everything else.
 *
 * Customers never see this — the caller must gate on `isAdmin || isOwner`
 * before rendering.
 */
export function OwnerToolbar({
  business,
  onEdit,
  onAssignOwner,
  onClaimQR,
  onDelete,
  onToggleGoogleReviews,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const isClaimed = !!business?.is_claimed;
  const gReviewsOff = !!business?.google_reviews_disabled;

  const actions = [
    { key: "edit", label: "Edit", icon: Pencil, onClick: onEdit, primary: true },
    { key: "assign", label: "Assign owner", icon: UserCheck, onClick: onAssignOwner },
    ...(!isClaimed ? [{ key: "qr", label: "Claim QR", icon: QrCode, onClick: onClaimQR }] : []),
    {
      key: "gReviews",
      label: gReviewsOff ? "Enable Google reviews" : "Disable Google reviews",
      icon: gReviewsOff ? Star : StarOff,
      onClick: onToggleGoogleReviews,
    },
    { key: "delete", label: "Delete", icon: Trash2, onClick: onDelete, danger: true },
  ].filter((a) => typeof a.onClick === "function");

  return (
    <section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-3"
      data-testid="owner-toolbar"
    >
      {/* Desktop / tablet: compact icon toolbar */}
      <div className="hidden sm:flex items-center justify-end gap-2 flex-wrap">
        {actions.map((a) => (
          <button
            key={a.key}
            type="button"
            onClick={a.onClick}
            title={a.label}
            data-testid={`owner-action-${a.key}`}
            className={
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition " +
              (a.danger
                ? "border-red-200 text-red-600 bg-white hover:bg-red-50"
                : a.primary
                  ? "border-tbc-navy bg-tbc-navy text-white hover:bg-tbc-navy/90"
                  : "border-gray-200 text-tbc-navy bg-white hover:border-tbc-navy/30")
            }
          >
            <a.icon className="w-3.5 h-3.5" strokeWidth={1.8} />
            {a.label}
          </button>
        ))}
      </div>

      {/* Mobile: Edit + Manage menu */}
      <div className="flex sm:hidden items-center justify-end gap-2 relative">
        <button
          type="button"
          onClick={onEdit}
          data-testid="owner-action-edit-mobile"
          className="inline-flex items-center gap-1.5 rounded-full bg-tbc-navy text-white px-4 py-2 text-xs font-medium"
        >
          <Pencil className="w-3.5 h-3.5" /> Edit
        </button>
        <div ref={menuRef} className="relative">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            data-testid="owner-action-manage-mobile"
            className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white text-tbc-navy px-3 py-2 text-xs font-medium"
          >
            <MoreHorizontal className="w-4 h-4" /> Manage
          </button>
          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 top-full mt-2 w-56 rounded-xl border border-gray-100 bg-white shadow-lg py-1 z-30"
              data-testid="owner-manage-menu"
            >
              {actions.filter((a) => !a.primary).map((a) => (
                <button
                  key={a.key}
                  type="button"
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); a.onClick?.(); }}
                  className={
                    "w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left " +
                    (a.danger ? "text-red-600 hover:bg-red-50" : "text-tbc-navy hover:bg-tbc-cream")
                  }
                  data-testid={`owner-menu-${a.key}`}
                >
                  <a.icon className="w-4 h-4" /> {a.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default OwnerToolbar;
